"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function DirectAnswerBlock() {
  const {
    ref: quoteRef,
    isVisible: quoteVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.3
  });
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.4
  });
  const {
    ref: pillarsRef,
    isVisible: pillarsVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const quoteLines = [{
    words: ["We", "do", "not"],
    italic: false
  }, {
    words: ["mark", "parts."],
    italic: false
  }, {
    words: ["We"],
    italic: false,
    italicWord: "engineer"
  }, {
    words: ["solutions", "that"],
    italic: false
  }, {
    words: ["endure", "time."],
    italic: false
  }];
  const allWords: {
    text: string;
    italic: boolean;
  }[] = [];
  allWords.push({
    text: "We",
    italic: false
  });
  allWords.push({
    text: "do",
    italic: false
  });
  allWords.push({
    text: "not",
    italic: false
  });
  allWords.push({
    text: "mark",
    italic: false
  });
  allWords.push({
    text: "parts.",
    italic: false
  });
  allWords.push({
    text: "We",
    italic: false
  });
  allWords.push({
    text: "engineer",
    italic: true
  });
  allWords.push({
    text: "solutions",
    italic: false
  });
  allWords.push({
    text: "that",
    italic: false
  });
  allWords.push({
    text: "endure",
    italic: false
  });
  allWords.push({
    text: "time.",
    italic: false
  });
  const lineBreakAfter = [2, 4, 6, 8];
  const pillars = [{
    number: "I.",
    title: "Accuracy",
    body: "Every mark is calibrated to micron-level tolerances. We utilize advanced laser systems to ensure that every identification tag meets the most rigorous industrial standards, providing clarity that remains legible in the harshest environments."
  }, {
    number: "II.",
    title: "Durability",
    body: "Our materials are selected for their resilience against heat, chemicals, and abrasion. From stainless steel to industrial-grade phenolics, we ensure that your critical infrastructure labeling survives the test of time and operational stress."
  }, {
    number: "III.",
    title: "Compliance",
    body: "We navigate the complexities of industry regulations so you don't have to. Whether it is UL 508A standards or arc flash safety requirements, our team ensures every component is compliant, certified, and ready for immediate installation."
  }];
  return <section data-section-id="4287" id="direct-answer-block" className="relative overflow-hidden bg-background text-foreground py-[200px] px-6 md:px-12">
      {}
      <div className="hidden md:block absolute left-6 top-1/2 -translate-y-1/2 origin-center -rotate-90 text-[10px] font-default uppercase tracking-[0.4em] text-primary">
        Precision
      </div>
      <div className="hidden md:block absolute right-6 top-1/2 -translate-y-1/2 origin-center rotate-90 text-[10px] font-default uppercase tracking-[0.4em] text-primary">
        Engineering
      </div>

      <div className="max-w-5xl mx-auto">
        {}
        <div ref={headerRef} className={`text-center transition-all duration-1000 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <p className="text-[11px] font-default uppercase tracking-[0.45em] mb-24 text-primary">
            — Our Engineering Standards
          </p>
        </div>

        {}
        <div ref={quoteRef} className="text-center">
          <blockquote className="font-serif font-light leading-[1.1] tracking-tight text-accent" style={{
          fontSize: "clamp(48px, 8vw, 96px)"
        }}>
            {(() => {
            const elements: React.ReactNode[] = [];
            let wordIdx = 0;
            allWords.forEach((w, i) => {
              elements.push(<span key={`w-${i}`} data-index={i} className={`inline-block transition-all ease-out ${w.italic ? "italic" : ""} ${quoteVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"}`} style={{
                transitionDuration: "900ms",
                transitionDelay: `${i * 80}ms`,
                color: w.italic ? "var(--primary)" : "var(--accent)"
              }}>
                    {w.text}
                  </span>);
              if (i < allWords.length - 1) {
                if (lineBreakAfter.includes(i)) {
                  elements.push(<br key={`br-${i}`} />);
                } else {
                  elements.push(<span key={`sp-${i}`}> </span>);
                }
              }
              wordIdx++;
            });
            return elements;
          })()}
          </blockquote>

          {}
          <div className={`mx-auto mt-20 h-px w-20 transition-all duration-1000 ease-out bg-primary ${quoteVisible ? "opacity-100 scale-x-100" : "opacity-0 scale-x-0"}`} style={{
          transitionDelay: "1100ms"
        }} />

          {}
          <p className={`mt-8 text-[11px] font-default uppercase tracking-[0.4em] transition-all duration-1000 ease-out text-primary ${quoteVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2"}`} style={{
          transitionDelay: "1300ms"
        }}>
            — Industrial Standards, Vol. I
          </p>
        </div>

        {}
        <div ref={pillarsRef} className="mt-32 md:mt-40 grid grid-cols-1 md:grid-cols-3 gap-px bg-primary/25">
          {pillars.map((pillar, i) => <div key={i} data-index={i} className={`bg-card p-8 md:p-10 transition-all duration-1000 ease-out ${pillarsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`} style={{
          transitionDelay: `${i * 200}ms`
        }}>
              <p className="font-serif text-base italic mb-4 text-primary">
                {pillar.number}
              </p>
              <h3 className="font-default text-[16px] font-medium mb-5 tracking-wide text-accent">
                {pillar.title}
              </h3>
              <p className="font-serif text-[15px] leading-relaxed font-light text-foreground">
                {pillar.body}
              </p>
            </div>)}
        </div>
      </div>
    </section>;
}